import api from '@/helpers/api';

import type { LoadedCompany } from '@/components/dropdown/types';
import type {
	AddCompanyAddressResponse,
	AddCompanyContactResponse,
	FetchCompanyAddressResponse,
	FetchMissionsParams,
	SearchCompaniesParams,
	SearchCompaniesResponse,
} from '@/modules/clients/types';
import type { IAddress } from '@/types/address';
import type { Pagination } from '@/types/common';
import type { CompanyReviews, ICompanyDB } from '@/types/company';
import type { Contact } from '@/types/contact';
import type { EContractType } from '@/types/contract';
import type { IMissionList, MissionStatus } from '@/types/mission';

interface ITotalFilter {
	status: Record<MissionStatus, number> | null;
	contractType: Record<EContractType, number> | null;
	jobs: { label: string; value: string; count: number }[] | null;
	collaborators: { label: string; value: string; count: number }[] | null;
}

interface LoadMissionsCompanyParams {
	page: number;
	limit: number;
}

interface CompanyFromAPI {
	addressCity: string;
	addressComplement: string;
	addressCountry: string;
	addressDepartment: string | null;
	addressLatitude: number;
	addressLongitude: number;
	addressRegion: string | null;
	addressStreet: string;
	addressZip: string;
	agencyNote: string | null;
	besoinArchivedCount: number;
	besoinOpenCount: number;
	businessSectorId: string | null;
	businessSectorName: string | null;
	contactsCount: number;
	createdAt: string;
	description: string | null;
	email: string;
	evoliaId: string;
	hasPortalAccess: boolean;
	id: string;
	lastMissionDate: string | null;
	linkedAgencyIds: string[];
	nafCode: string;
	name: string;
	ownerAgencyId: string;
	ownerAgencyName: string;
	phone: string;
	responsibleId: string | null;
	responsibleName: string;
	siret: string;
	updatedAt: string;
}

export type CompanyHistorySource = 'missions' | 'besoins' | 'active-proposals';

const emptyPaginatedResponse = (
	params: LoadMissionsCompanyParams
): Pagination<IMissionList> => ({
	page: params.page,
	limit: params.limit,
	total: 0,
	totalPages: 0,
	items: [],
});

const normalizeCompanyHistoryResponse = (
	data: Record<string, unknown>,
	params: LoadMissionsCompanyParams
): Pagination<IMissionList> => {
	if (!data) {
		return emptyPaginatedResponse(params);
	}

	// Handle both paginated and array responses
	if (Array.isArray(data)) {
		return {
			page: params.page,
			limit: params.limit,
			total: data.length,
			totalPages: data.length > 0 ? Math.ceil(data.length / params.limit) : 0,
			items: data,
		};
	}

	const items = Array.isArray(data.items)
		? data.items
		: Array.isArray(data.data)
			? data.data
			: [];
	const limit = typeof data.limit === 'number' ? data.limit : params.limit;
	const total =
		typeof data.total === 'number'
			? data.total
			: typeof data.totalItems === 'number'
				? data.totalItems
				: items.length;
	const totalPages =
		typeof data.totalPages === 'number'
			? data.totalPages
			: total > 0
				? Math.ceil(total / limit)
				: 0;

	return {
		page: typeof data.page === 'number' ? data.page : params.page,
		limit,
		total,
		totalPages,
		items,
	};
};

const NULL_LIKE_PATTERN = /^(null\s*)+$/i;

const sanitizeText = (value: string | null | undefined): string | null => {
	if (!value || NULL_LIKE_PATTERN.test(value.trim())) return null;
	return value;
};

const isNoResultError = (error: unknown) => {
	const err = error as Record<string, Record<string, unknown>> | undefined;
	const status =
		err?.response?.status ??
		err?.status ??
		(err?.fullErr as Record<string, Record<string, unknown>>)?.response?.status;
	return status === 404;
};

const fetchCompany = async (
	companyId: string
): Promise<ICompanyDB & { contacts: Contact[] }> => {
	const response = await api.request<Record<string, unknown>>({
		url: `/companies/${companyId}`,
		method: 'GET',
	});
	const data: Record<string, unknown> = response.data;

	// Build addresses array from flat address fields
	const addresses = [];
	if (data.addressStreet || data.addressCity) {
		addresses.push({
			name: 'Adresse principale',
			street: data.addressStreet,
			city: data.addressCity,
			zip: data.addressZip,
			department: data.addressDepartment,
			isMain: true,
		});
	}

	// Transform API response to ICompanyDB format
	return {
		...data,
		id: data.id,
		note: sanitizeText(data.agencyNote as string | null | undefined),
		description: sanitizeText(data.description as string | null | undefined),
		mainAddress: {
			street: data.addressStreet,
			city: data.addressCity,
			zip: data.addressZip,
			department: data.addressDepartment,
		},
		addresses,
		phone: data.phone,
		email: data.email,
		jobIds: Array.isArray(data.jobIds) ? data.jobIds : [],
		contacts: data.contacts,
	} as unknown as ICompanyDB & { contacts: Contact[] };
};

const fetchCompanyReviews = async (
	companyId: string
): Promise<CompanyReviews> => {
	const response = await api.request<CompanyReviews>({
		url: `/companies/reviews/${companyId}`,
		method: 'GET',
	});
	return response.data;
};

const fetchCompanyContacts = async (companyId: string): Promise<Contact[]> => {
	const response = await api.request<Record<string, unknown>[]>({
		url: `/companies/${companyId}/contacts`,
		method: 'GET',
	});
	return response.data as unknown as Contact[];
};

const fetchMissions = async (params: FetchMissionsParams) => {
	const response = await api.request<{
		missions: IMissionList[];
		totalFilters: ITotalFilter;
		total: number;
	}>({
		url: '/missions/',
		method: 'GET',
		params,
	});
	return response.data;
};

const addCompanyContact = async (companyId: string, contact: Contact) => {
	const response = await api.request<AddCompanyContactResponse>({
		url: `/companies/${companyId}/contacts`,
		method: 'POST',
		data: { ...contact, companiesList: contact.companiesList },
	});
	return response.data;
};

const deleteCompanyContact = async (companyId: string, contactId: string) => {
	const response = await api.request({
		url: `/companies/${companyId}/contacts/${contactId}`,
		method: 'DELETE',
	});
	return response.data;
};

const updateCompanyContact = async (
	companyId: string,
	contactId: string,
	contactData: Contact
) => {
	const response = await api.request({
		url: `/companies/${companyId}/contacts/${contactId}`,
		method: 'PATCH',
		data: contactData,
	});
	return response.data;
};

const editCompany = async (companyId: string, company: ICompanyDB) => {
	const response = await api.request({
		url: `/companies/${companyId}`,
		method: 'PATCH',
		data: company,
	});
	return response.data;
};

const addCompanyAddress = async (companyId: string, address: IAddress) => {
	const { zip, name, ...rest } = address;
	const response = await api.request<AddCompanyAddressResponse>({
		url: `/companies/${companyId}/addresses`,
		method: 'POST',
		data: { ...rest, zipCode: zip, label: name },
	});
	return response.data;
};

const normalizeCountryCode = (
	value: string | undefined
): string | undefined => {
	if (!value) return undefined;
	if (value.length <= 2) return value.toUpperCase();
	return undefined;
};

const updateCompanyAddress = async (
	companyId: string,
	addressId: string,
	address: IAddress
) => {
	const response = await api.request({
		url: `/companies/${companyId}/addresses/${addressId}`,
		method: 'PATCH',
		data: {
			label: address.name,
			street: address.street,
			city: address.city,
			zipCode: address.zip,
			country: normalizeCountryCode(address.countryCode),
		},
	});
	return response.data;
};

const deleteCompanyAddress = async (companyId: string, addressId: string) => {
	const response = await api.request({
		url: `/companies/${companyId}/addresses/${addressId}`,
		method: 'DELETE',
	});
	return response.data;
};

const loadMissionsCompany = async (
	companyId: string,
	params: LoadMissionsCompanyParams
): Promise<Pagination<IMissionList>> => {
	const response = await api.request<Pagination<IMissionList>>({
		url: `/companies/${companyId}/missions`,
		method: 'GET',
		params,
	});
	return response.data;
};

const loadMissionsForCompany = async (
	companyId: string,
	params: LoadMissionsCompanyParams
): Promise<Pagination<IMissionList>> => {
	const response = await api.request<Record<string, unknown>>({
		url: `/missions/company/${companyId}`,
		method: 'GET',
		params,
	});
	return normalizeCompanyHistoryResponse(response.data, params);
};

const loadCompanyHistoryForSource = async (
	companyId: string,
	source: CompanyHistorySource,
	params: LoadMissionsCompanyParams
): Promise<Pagination<IMissionList>> => {
	try {
		const response = await api.request<Record<string, unknown>>({
			url: `/${source}/company/${companyId}`,
			method: 'GET',
			params,
		});
		return normalizeCompanyHistoryResponse(response.data, params);
	} catch (error) {
		if (isNoResultError(error)) {
			return emptyPaginatedResponse(params);
		}
		throw error;
	}
};

const fetchContactBySalesforceId = async (
	salesforceId: string
): Promise<Contact> => {
	const response = await api.request<Contact>({
		url: `/companies/contacts/salesforce/${salesforceId}`,
		method: 'GET',
	});
	return response.data;
};

const fetchCompanyAddresses = async (
	companyId: string
): Promise<FetchCompanyAddressResponse[]> => {
	const response = await api.request<FetchCompanyAddressResponse[]>({
		url: `/companies/${companyId}/addresses`,
		method: 'GET',
	});
	return response.data;
};

const normalizeCompanyListItem = (company: CompanyFromAPI): LoadedCompany => {
	const address = {
		street: company.addressStreet,
		city: company.addressCity,
		zip: company.addressZip,
		country: company.addressCountry,
		department: company.addressDepartment,
		latitude: company.addressLatitude,
		longitude: company.addressLongitude,
		region: company.addressRegion,
		complement: company.addressComplement,
	};
	return {
		address,
		id: company.id,
		name: company.name,
		slug: company.name.toLowerCase().replace(/ /g, '-'),
		businessSector: company.businessSectorName,
		collaborator: company.responsibleName,
		contact: company.email,
		phone: company.phone,
		email: company.email,
		lastMission: company.lastMissionDate ?? '',
		createdAt: company.createdAt,
		website: '',
	};
};

const loadCompanies = async (params: Record<string, unknown>) => {
	const response = await api.request<Pagination<CompanyFromAPI>>({
		url: '/companies',
		method: 'GET',
		params,
	});
	const data = response.data;

	return {
		...data,
		items: data.items.map(normalizeCompanyListItem),
	};
};

const loadCompany = async (companyId: string) => {
	const response = await api.request({
		url: `/companies/${companyId}`,
		method: 'GET',
	});
	return response.data;
};

const getCompanyContact = async (
	companyId: string,
	query: Record<string, unknown>
) => {
	const response = await api.request({
		url: `/companies/${companyId}/contacts`,
		method: 'GET',
		params: query,
	});
	return response.data;
};

const searchCompanies = async (
	params: SearchCompaniesParams,
	signal?: AbortSignal
): Promise<SearchCompaniesResponse> => {
	const response = await api.request({
		url: '/companies/search',
		method: 'GET',
		params,
		signal,
	});
	return response.data;
};

export const clientService = {
	fetchCompany,
	fetchCompanyReviews,
	fetchCompanyContacts,
	fetchCompanyAddresses,
	fetchMissions,
	addCompanyContact,
	addCompanyAddress,
	updateCompanyAddress,
	deleteCompanyAddress,
	deleteCompanyContact,
	updateCompanyContact,
	editCompany,
	loadMissionsCompany,
	loadMissionsForCompany,
	loadCompanyHistoryForSource,
	fetchContactBySalesforceId,
	loadCompanies,
	loadCompany,
	getCompanyContact,
	searchCompanies,
};
